import styles from "./AppSidebar.module.scss";
import { ChevronsLeft, Inbox, PanelLeft, Plus } from "lucide-react";
import F from "../../gui-bits/assets/visuals/F.svg";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "../ui/sidebar";
import { cn } from "@fsg/utils";
import { Label } from "@gui";
import { FsgLogo } from "src/gui-bits/assets/visuals/Logo";
import { AppSwitcher } from "@gui/app-switcher";
import { NavItemLink } from "../Navbar";
import { usePathname } from "next/navigation";
import { useNavRoutes } from "@app/hooks";
import { useEffect } from "react";
import Image from "next/image";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { open, isMobile, toggleSidebar, setOpen, setOpenMobile } =
    useSidebar();
  const pathname = usePathname();
  const routes = useNavRoutes();
  const isLinkActive = (href: string) =>
    href === "/" ? pathname === href : pathname.startsWith(href);

  useEffect(() => {
    // Closes the sheet after a link is clicked, which redirects, meaning pathname changes
    setOpenMobile(false);
  }, [pathname]);

  return (
    <Sidebar {...props}>
      <SidebarHeader>
        <div className={cn(styles["grid-stack"], "py-xs")}>
          <div
            className={cn(
              styles["stack"],
              "flex items-center gap-2xs transition-[opacity,transform] duration-200 ease-linear z-10",
              {
                "opacity-0 scale-95": !open,
              }
            )}
          >
            <FsgLogo />
            <Label className="!px-[8px] text-[11px] tracking-[0.01rem] py-[4px]">
              ENGAGE
            </Label>
            {/* <AppSwitcher className="py-[2px] px-xs h-[24px] flex items-center text-[11px]" appName="ENGAGE" /> */}
          </div>
          <div
            className={cn(
              styles["stack"],
              "transition-opacity duration-200 ease-linear",
              { "opacity-0": open || isMobile }
            )}
          >
            <Image
              src={F}
              alt="FSG Logo"
              className="w-[24px] h-[24px]"
              width={24}
              height={24}
            />
          </div>
        </div>
      </SidebarHeader>
      <SidebarContent className="h-full">
        <div className="flex flex-col gap-2xs h-full justify-between">
          <ul className="flex flex-col gap-sm justify-between">
            {routes.map((item, index) => (
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  key={`nav-${index}`}
                  isActive={isLinkActive(item.href)}
                  className={`data-[active=true]:bg-color-active data-[active=true]:text-primary`}
                ></SidebarMenuButton>
                <NavItemLink {...item} key={item.href} isOpen={open}>
                  <item.icon />
                  <span className="font-bold">{item.label}</span>
                </NavItemLink>
              </SidebarMenuItem>
            ))}
          </ul>
        </div>
      </SidebarContent>
      <SidebarRail />
      <SidebarFooter className={styles["sidebar-footer"]}>
        <SidebarMenuButton
          className={cn(styles["sidebar-trigger"], "rounded-xs")}
          onClick={toggleSidebar}
        >
          <PanelLeft width={16} height={16} />
        </SidebarMenuButton>
      </SidebarFooter>
    </Sidebar>
  );
}
